import { FCElement } from '@fc/component';

const VIDEO_SOURCE_DATA_KEY = 'videoSource';
const ACTIVE_MEDIA_DATA_KEY = 'fitActiveMedia';
const DEFER_NEAR_VIEWPORT_DATA_KEY = 'fitDeferUntilNearViewport';
const VIDEO_LOADING_DISABLED_DATA_KEY = 'fitVideoLoadingDisabled';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const VIDEO_STATE_CHANGE_EVENT = 'fit:video-state-change';
const HERO_VIDEO_SELECTOR = '[data-fit-hero-video] video-backdrop';
const NEAR_VIEWPORT_MARGIN = '400px 0px';

/**
 * Reads source URL from a source element dataset.
 * @param {HTMLElement} sourceEl
 * @returns {string}
 */
function getSourceUrl(sourceEl) {
  const raw = sourceEl?.dataset?.[VIDEO_SOURCE_DATA_KEY];
  if (typeof raw !== 'string') return '';
  return raw.trim();
}

/**
 * Applies source URL to <source> element if present.
 * @param {HTMLElement} sourceEl
 */
function hydrateSourceElement(sourceEl) {
  const srcUrl = getSourceUrl(sourceEl);
  if (!srcUrl) return;
  if (sourceEl.getAttribute('src') === srcUrl) return;
  sourceEl.setAttribute('src', srcUrl);
}

/**
 * Applies dataset-provided source URLs to all video source nodes.
 * @param {HTMLElement[]} sourceElements
 */
function hydrateSourceList(sourceElements) {
  for (const sourceEl of sourceElements) {
    hydrateSourceElement(sourceEl);
  }
}

/**
 * Aligns mutable media properties with the Liquid-rendered attributes before
 * loading dynamically hydrated sources.
 * @param {HTMLVideoElement} videoElement
 */
function syncVideoBooleanProperties(videoElement) {
  videoElement.muted = videoElement.defaultMuted;
  videoElement.loop = videoElement.hasAttribute('loop');
  videoElement.controls = videoElement.hasAttribute('controls');
  videoElement.playsInline = videoElement.hasAttribute('playsinline');
}

/**
 * Guards ref types required to initialize video background.
 * @param {HTMLVideoElement | unknown} videoElement
 * @param {unknown} videoSources
 * @returns {boolean}
 */
function canInitialize(videoElement, videoSources) {
  if (!(videoElement instanceof HTMLVideoElement)) return false;
  if (!Array.isArray(videoSources)) return false;
  return true;
}

export class VideoBackgroundComponent extends FCElement {
  requiredRefs = ['videoSources', 'videoElement'];

  #sourcesHydrated = false;
  #videoLoaded = false;
  #nearViewport = true;
  #viewportObserver = null;
  #userPaused = false;

  get videoElement() {
    return this.refs.videoElement instanceof HTMLVideoElement ? this.refs.videoElement : null;
  }

  get paused() {
    return this.videoElement?.paused ?? true;
  }

  connectedCallback() {
    super.connectedCallback();

    const { videoSources } = this.refs;
    const videoElement = this.videoElement;
    if (!canInitialize(videoElement, videoSources)) return;
    syncVideoBooleanProperties(videoElement);
    this.#bindVideoEvents(videoElement);
    this.#bindActivationEvents();
    this.#bindViewportActivation();
    this.#syncActivation();
  }

  disconnectedCallback() {
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = null;
    super.disconnectedCallback();
  }

  play() {
    const videoElement = this.videoElement;
    const { videoSources } = this.refs;
    if (!(videoElement instanceof HTMLVideoElement)) {
      return Promise.resolve();
    }
    if (!canInitialize(videoElement, videoSources)) {
      return Promise.resolve();
    }
    if (!this.#canLoadVideo) {
      return Promise.resolve();
    }

    this.#userPaused = false;
    this.#prepareVideo(videoElement, videoSources);
    return videoElement.play();
  }

  pause() {
    this.#userPaused = true;
    this.videoElement?.pause();
  }

  togglePlayback() {
    if (this.paused) {
      void this.play().catch(() => this.#syncPlayingState());
    } else {
      this.pause();
    }
  }

  #bindActivationEvents() {
    const reducedMotionQuery = window.matchMedia?.(REDUCED_MOTION_QUERY);
    if (reducedMotionQuery) {
      this.addManagedEventListener(reducedMotionQuery, 'change', this.#syncActivation);
    }

    const activeMediaQuery = this.#activeMediaQuery;
    if (activeMediaQuery) {
      this.addManagedEventListener(activeMediaQuery, 'change', this.#syncActivation);
    }
  }

  get #activeMediaQuery() {
    const query = this.dataset?.[ACTIVE_MEDIA_DATA_KEY]?.trim();
    if (!query || !window.matchMedia) return null;

    try {
      return window.matchMedia(query);
    } catch {
      return null;
    }
  }

  get #allowsMotion() {
    const reducedMotionQuery = window.matchMedia?.(REDUCED_MOTION_QUERY);
    return !reducedMotionQuery?.matches;
  }

  get #isActiveMedia() {
    const mediaQuery = this.#activeMediaQuery;
    return !mediaQuery || mediaQuery.matches;
  }

  get #canLoadVideo() {
    const loadingDisabled = this.dataset?.[VIDEO_LOADING_DISABLED_DATA_KEY] !== undefined;
    return !loadingDisabled && this.#allowsMotion && this.#isActiveMedia && this.#nearViewport;
  }

  #bindViewportActivation() {
    const shouldDefer = this.dataset?.[DEFER_NEAR_VIEWPORT_DATA_KEY] !== undefined;
    if (!shouldDefer || typeof IntersectionObserver === 'undefined') {
      this.#nearViewport = true;
      return;
    }

    this.#nearViewport = false;
    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.#nearViewport = entry.isIntersecting;
        this.#syncActivation();
      },
      { rootMargin: NEAR_VIEWPORT_MARGIN },
    );
    this.#viewportObserver.observe(this);
  }

  #hydrateVideoSources(videoSources) {
    if (this.#sourcesHydrated) return;
    hydrateSourceList(videoSources);
    this.#sourcesHydrated = true;
  }

  #prepareVideo(videoElement, videoSources) {
    this.#hydrateVideoSources(videoSources);
    if (this.#videoLoaded) return;
    videoElement.load();
    this.#videoLoaded = true;
  }

  #syncActivation = () => {
    const { videoSources } = this.refs;
    const videoElement = this.videoElement;
    if (!canInitialize(videoElement, videoSources)) return;

    if (!this.#canLoadVideo) {
      videoElement.pause();
      this.#syncPlayingState();
      return;
    }

    this.#prepareVideo(videoElement, videoSources);
    this.#syncPlayingState();
    if (videoElement.autoplay && !this.#userPaused) {
      this.#startAutoplay(videoElement);
    }
  };

  async #startAutoplay(videoElement) {
    try {
      await videoElement.play();
    } catch {
      this.#syncPlayingState();
    }
  }

  #bindVideoEvents(videoElement) {
    this.addManagedEventListener(videoElement, 'keydown', this.#handleVideoKeydown);
    this.addManagedEventListener(videoElement, 'playing', this.#syncPlayingState);
    this.addManagedEventListener(videoElement, 'play', this.#syncPlayingState);
    this.addManagedEventListener(videoElement, 'pause', this.#syncPlayingState);
    this.addManagedEventListener(videoElement, 'ended', this.#syncPlayingState);
  }

  #handleVideoKeydown = (event) => {
    const isSpace = event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space';
    if (!isSpace) return;

    const videoElement = this.videoElement;
    if (!(videoElement instanceof HTMLVideoElement)) return;

    event.preventDefault();
    if (videoElement.paused || videoElement.ended) {
      void this.play();
    } else {
      this.pause();
    }
    this.#syncPlayingState();
  };

  #syncPlayingState = () => {
    const videoElement = this.videoElement;
    if (!(videoElement instanceof HTMLVideoElement)) return;

    const isPlaying = !videoElement.paused && !videoElement.ended;
    this.toggleAttribute('data-playing', isPlaying);
    this.#syncAccessibleLabel(videoElement, isPlaying);
    this.#dispatchPlaybackState();
  };

  #syncAccessibleLabel(videoElement, isPlaying) {
    const playLabel = videoElement.dataset.playLabel?.trim();
    const pauseLabel = videoElement.dataset.pauseLabel?.trim();
    if (!playLabel || !pauseLabel || !videoElement.hasAttribute('aria-label')) return;
    videoElement.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
  }

  #dispatchPlaybackState = () => {
    const videoElement = this.videoElement;
    if (!(videoElement instanceof HTMLVideoElement)) return;

    this.dispatchEvent(
      new CustomEvent(VIDEO_STATE_CHANGE_EVENT, {
        bubbles: true,
        detail: {
          paused: videoElement.paused,
          ended: videoElement.ended,
        },
      }),
    );
  };
}

export class HeroBannerVideoControl extends FCElement {
  get videoControl() {
    return this.refs.videoControl instanceof HTMLButtonElement ? this.refs.videoControl : null;
  }

  connectedCallback() {
    super.connectedCallback();

    const control = this.videoControl;
    if (!control) return;

    this.addManagedEventListener(control, 'click', this.#togglePlayback);
    this.addManagedEventListener(this, VIDEO_STATE_CHANGE_EVENT, this.#syncControl);

    const reducedMotionQuery = window.matchMedia?.(REDUCED_MOTION_QUERY);
    if (reducedMotionQuery) {
      this.addManagedEventListener(reducedMotionQuery, 'change', this.#syncControl);
    }

    for (const query of this.#activeMediaQueries) {
      this.addManagedEventListener(query, 'change', this.#syncControl);
    }

    queueMicrotask(this.#syncControl);
  }

  get #activeMediaQueries() {
    const queries = new Map();
    if (!window.matchMedia) return queries.values();

    for (const backdrop of this.querySelectorAll(HERO_VIDEO_SELECTOR)) {
      const query = backdrop.dataset?.[ACTIVE_MEDIA_DATA_KEY]?.trim();
      if (!query || queries.has(query)) continue;
      try {
        queries.set(query, window.matchMedia(query));
      } catch {
        // Invalid media queries are treated as inactive by #matchesActiveMedia.
      }
    }
    return queries.values();
  }

  get #activeVideoBackdrop() {
    return Array.from(this.querySelectorAll(HERO_VIDEO_SELECTOR)).find((backdrop) => this.#matchesActiveMedia(backdrop)) ?? null;
  }

  #matchesActiveMedia(backdrop) {
    const query = backdrop.dataset?.[ACTIVE_MEDIA_DATA_KEY]?.trim();
    if (!query) return true;
    if (!window.matchMedia) return false;
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  }

  #togglePlayback = async (event) => {
    event.preventDefault();
    const backdrop = this.#activeVideoBackdrop;
    if (!(backdrop instanceof VideoBackgroundComponent)) return;

    if (backdrop.paused) {
      try {
        await backdrop.play();
      } catch {
        // Autoplay policy or media errors leave the control in its paused state.
      }
    } else {
      backdrop.pause();
    }
    this.#syncControl();
  };

  #syncControl = () => {
    const control = this.videoControl;
    if (!control) return;

    const reducedMotion = window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches ?? false;
    const backdrop = this.#activeVideoBackdrop;
    if (reducedMotion || !(backdrop instanceof VideoBackgroundComponent)) {
      control.hidden = true;
      control.removeAttribute('data-playing');
      return;
    }

    const isPlaying = !backdrop.paused;
    const playLabel = control.dataset.playLabel?.trim() || '';
    const pauseLabel = control.dataset.pauseLabel?.trim() || '';
    control.hidden = false;
    control.toggleAttribute('data-playing', isPlaying);
    control.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
  };
}

if (!customElements.get('video-backdrop')) {
  customElements.define('video-backdrop', VideoBackgroundComponent);
}

if (!customElements.get('hero-banner')) {
  customElements.define('hero-banner', HeroBannerVideoControl);
}
